const article = {
	slug: "mentorship-momentum",
	title: "Mentorship, Momentum, and the Drive to Level Up",
	date: "20 July 2025",
	updated: null,
	description:
		"Rebuilding physical strength at the gym and technical depth at work are the same project: showing up without motivation, under a mentor, and growing past comfort.",
	keywords: [
		"mentorship",
		"engineering leadership",
		"personal growth",
		"career",
	],
	tldr: "Growth and comfort rarely coexist. In the gym and at work, the pattern is identical: a mentor, consistency over motivation, and the willingness to unlearn old habits.",
	readingTime: null,
	provenance: "Adapted from my LinkedIn post, July 2025.",
	provenanceUrl: null,
	series: null,
	artifact: null,
	markdown: `> "Growth and Comfort rarely coexist." A takeaway from an Ascend NJ event on how to adopt changes that support personal growth.

This past week, at that Ascend NJ event, a key idea resonated deeply: **growth and comfort rarely coexist.** It perfectly articulates the uncomfortable but necessary evolution I am experiencing.

Consider a recent morning. 6:25 AM. Chalked hands. 200 lbs off the floor. That was the scene right after a brutal set of Romanian deadlifts. Exhausted, sore, but completely focused.

Just a few hours later, I was deep in back-to-back meetings discussing architecture trade-offs, system outages, and critical delivery timelines. Lately, I have been thinking a lot about what it truly means to stay strong physically, mentally, and technically.

Professionally, it brings to mind classics like *Joel on Software*, *The Pragmatic Engineer*, and *Clean Code*, a few books that shaped my early understanding of building systems.

As an engineering manager, I have found myself increasingly pulled into strategy, alignment, and people leadership. It is incredibly rewarding, but it has also been tough to realize slowly how far I have drifted from the core of consistent hands-on building. I genuinely miss the luxury of focused problem solving, the solo and pair debugging sessions, and the satisfaction of shipping something tangible at scale.

My anchor in both worlds? **Mentorship.** I have been training at the gym under Coach Joel for a few months now. He has been a powerful reminder that progress demands patience, consistency, and a willingness to unlearn old habits.

His advice: *just keep walking through those gym doors and don't rely on motivation.* That is golden advice on showing up even when you don't feel like it. He has helped me rebuild strength from the ground up, and his approach mirrors the best technical mentors I have ever had: kind, motivating, direct, quietly relentless.

Mr. B, a soothing trusted voice at work, dropped this wisdom in our 1:1: **"Being a leader doesn't necessarily mean that you excel. It means you make your team excel."** I could not be more proud of my current leadership for always shooting high and leveling us all up in the process.

At work, I have been intentionally rebuilding my technical depth: relearning AWS concepts, diving deep into modern authentication processes which I had lost touch with for 10 years, and pushing myself to ask better questions, then answer them myself.

Both journeys are slow. Both test your ego. This is about momentum. Reclaiming parts of yourself you almost forgot you missed.

In the gym, I am rebuilding my form. At work, I am rebuilding deep focus and looking to re-excel at execution.

Speaking of leveling up, I recently learned that Coach Joel was a world number 2 in weightlifting some years ago. That bit of information made me stop doubting my own capabilities and push harder.

It is clear: great role models make you push yourself to grow past comfort.`,
};

export default article;
